import { nanoid } from 'nanoid';

import type { Item } from '@/api/gen/_model/item';
import type { MatchSet } from '@/api/gen/_model/matchSet';
import type { MultipleSet } from '@/api/gen/_model/multipleSet';
import type { OrderSet } from '@/api/gen/_model/orderSet';
import type { OxSet } from '@/api/gen/_model/oxSet';
import type { QuizSetData } from '@/api/gen/_model/quizSetData';
import type { SubjectiveSet } from '@/api/gen/_model/subjectiveSet';
import { sortItemsByOrderIds } from '@/shared/quiz/orderItems';

import { type BuildQuizListItem } from '../form/schema/BuildQuizListSchema';
import { getBlankQuizItem } from '../form/schema/getBlankQuizItem';

import { withCurriculumFingerprint } from './curriculumFingerprint';

/**
 * QuizSetData를 BuildSchema에 맞도록 형태를 변환합니다.
 * 서버와 논의하여, QuizSetDataV2로 migration을 진행하기를 권장합니다.
 * @param quizzes - Quiz set data
 * @returns Build schema input type
 */
export const adaptQuizSetDataToBuildSchema = (quizzes: QuizSetData[]) => {
  const newQuizList = quizzes.map(quiz => {
    const baseQuiz = {
      ...getBlankQuizItem(),
      //서버에서는 nullable 데이터가 null로 오는데, 클라에서는 optional의 경우 undefined여야 schema를 통과합니다.
      // 따라서 nullish 병합 연산자를 사용하여 undefined로 변환합니다.
      questionImageUrl: quiz.imageUrl ?? undefined,
      hint:
        quiz.hint && quiz.hint.value
          ? {
              type: quiz.hint.type as 'text' | 'image' | 'youtube',
              value: quiz.hint.value,
            }
          : undefined,
      // 편집 모드에서 저장된 성취기준을 폼 상태에 복원한다. 복원하지 않으면
      // submit 시 빈 리스트가 전송되어 서버의 기존 값이 제거된다.
      curriculumList: quiz.curriculumList ?? undefined,
      id: quiz.id,
    };

    // 복원 시점의 내용 지문을 찍어, 이후 문항이 편집되면 submit/스냅샷 직렬화에서
    // 성취기준을 제외할 수 있게 한다.
    return withCurriculumFingerprint(adaptQuizItemByType(quiz, baseQuiz));
  });

  return newQuizList;
};

const adaptQuizItemByType = (
  quiz: QuizSetData,
  baseQuiz: BuildQuizListItem,
): BuildQuizListItem => {
  switch (quiz.type) {
    case 'subjective':
      return {
        ...baseQuiz,
        question: quiz.subjectiveData?.question ?? '',
        questionRichText: quiz.subjectiveData?.questionRichText ?? undefined,
        type: 'subjective' as const,
        subjectiveData: adaptSubjectiveData(
          quiz.subjectiveData as SubjectiveSet,
        ),
      };
    case 'multiple':
      return {
        ...baseQuiz,
        question: quiz.multipleData?.question ?? '',
        questionRichText: quiz.multipleData?.questionRichText ?? undefined,
        type: 'multiple' as const,
        multipleData: adaptMultipleData(quiz.multipleData as MultipleSet),
      };
    case 'ox':
      return {
        ...baseQuiz,
        question: quiz.oxData?.question ?? '',
        questionRichText: quiz.oxData?.questionRichText ?? undefined,
        oxData: adaptOXData(quiz.oxData as OxSet),
        type: 'ox' as const,
      };
    case 'essay':
      return {
        ...baseQuiz,
        question: quiz.essayData?.question ?? '',
        questionRichText: quiz.essayData?.questionRichText ?? undefined,
        type: 'essay' as const,
      };
    case 'match':
      return {
        ...baseQuiz,
        question: quiz.matchData?.question ?? '',
        questionRichText: quiz.matchData?.questionRichText ?? undefined,
        type: 'match' as const,
        matchData: adaptMatchData(quiz.matchData as MatchSet),
      };
    case 'order':
      return {
        ...baseQuiz,
        question: quiz.orderData?.question ?? '',
        questionRichText: quiz.orderData?.questionRichText ?? undefined,
        type: 'order' as const,
        orderData: adaptOrderData(quiz.orderData),
      };
    default:
      throw new Error('Invalid quiz type');
  }
};

/**
 * 서버 `OrderSet` → 폼. `buildFormToApiTransformer` 의 정확한 역방향이다.
 *
 * 서버는 `items`(입력 순서)와 `correctOrderIds`(정답 순서)를 나눠 갖지만
 * 폼은 배열 순서 하나로만 정답을 표현한다. 그래서 정답 순서대로 재배열해서 돌려준다 —
 * 이 정렬을 빠뜨리면 수정 화면이 저장했던 것과 **다른 순서**를 보여주고,
 * 그대로 다시 저장하면 정답이 조용히 바뀐다.
 */
const adaptOrderData = (orderData?: OrderSet) => ({
  items: sortItemsByOrderIds(
    orderData?.items ?? [],
    orderData?.correctOrderIds,
  ).map(item => ({
    ...item,
    richText: item.richText ?? undefined,
    equation: item.equation ?? undefined,
    imageUrl: item.imageUrl ?? undefined,
  })),
});

const adaptMultipleData = (multipleData: MultipleSet) => {
  const choiceList = multipleData.choiceListV2.map((choice, index) => ({
    ...choice,
    key: nanoid(8),
    richText: choice.richText ?? undefined,
    imageUrl: choice.imageUrl ?? undefined,
    isAnswer: multipleData.answerList.includes(String(index)),
  }));

  return {
    choiceList,
  };
};

const adaptSubjectiveData = (subjectiveData: SubjectiveSet) => {
  return {
    answerList: subjectiveData.answerList.map(answer => ({
      key: nanoid(8),
      text: answer,
    })),
  };
};

const adaptOXData = (oxData: OxSet) => {
  return {
    answer: oxData.answer as 'o' | 'x',
  };
};

const adaptMatchData = (matchData: MatchSet) => {
  const leftItemsById = new Map(
    matchData.leftItems.map(item => [item.id, item]),
  );
  const rightItemsById = new Map(
    matchData.rightItems.map(item => [item.id, item]),
  );
  const alignedItems = matchData.answerPairs.map(pair => ({
    left: leftItemsById.get(pair.leftId),
    right: rightItemsById.get(pair.rightId),
  }));
  const canAlignRows =
    alignedItems.length === matchData.leftItems.length &&
    alignedItems.length === matchData.rightItems.length &&
    alignedItems.every(
      (pair): pair is { left: Item; right: Item } =>
        pair.left !== undefined && pair.right !== undefined,
    );
  const leftItems = canAlignRows
    ? alignedItems.map(pair => pair.left)
    : matchData.leftItems;
  const rightItems = canAlignRows
    ? alignedItems.map(pair => pair.right)
    : matchData.rightItems;

  return {
    leftItems: leftItems.map(item => ({
      ...item,
      richText: item.richText ?? undefined,
      equation: item.equation ?? undefined,
      imageUrl: item.imageUrl ?? undefined,
    })),
    rightItems: rightItems.map(item => ({
      ...item,
      richText: item.richText ?? undefined,
      equation: item.equation ?? undefined,
      imageUrl: item.imageUrl ?? undefined,
    })),
    answerPairs: matchData.answerPairs,
  };
};
